<template>
    <div class="row">
        <div class="col-12 form-group">
            <div class="row">

                <div class="col-4 mb-2">
                    <label class="control-label" for="category_divisionId">
                        {{resource('DivisionMiscellaneousCategoryEditModel_DivisionId_DisplayName')}}
                    </label>
                </div>

                <div class="col-8 mb-2">
                    <division-dropdown
                        v-if="divisions"
                        id="category_divisionId"
                        v-bind:divisionList="divisions"
                        v-model="category.divisionId"
                        :key="category.divisionId"
                    >
                    </division-dropdown>
                </div>

                <div class="col-4 mb-2">
                    <label class="control-label" for="category_name">
                        {{resource('DivisionMiscellaneousCategoryEditModel_Name_DisplayName')}}
                    </label>
                </div>

                <div class="col-8 mb-2">
                    <input id="category_name" class="form-control" type="text" v-model="category.name">
                    <required-error-message
                        v-if="nameIsMissing"
                        v-bind:field-name="resource('DivisionMiscellaneousCategoryEditModel_Name_DisplayName')" 
                    />
                </div>

                <div class="col-4 mb-2">
                    <label class="control-label" for="category_label">
                        {{resource('DivisionMiscellaneousCategoryEditModel_Label_DisplayName')}}
                    </label>
                </div>

                <div class="col-8 mb-2">
                    <input id="category_label" class="form-control" type="text" v-model="category.label">
                </div>

                <div class="col-4 mb-2">
                    <label class="control-label" for="category_sortOrder">
                        {{resource('DivisionMiscellaneousCategoryEditModel_SortOrder_DisplayName')}}
                    </label>
                </div>

                <div class="col-8 mb-2">
                    <input id="category_sortOrder" class="form-control" type="number" v-model.number="category.sortOrder">
                    <required-error-message
                            v-if="sortOrderIsMissing"
                            v-bind:field-name="resource('DivisionMiscellaneousCategoryEditModel_SortOrder_DisplayName')"
                    />
                </div>

                <div class="col-4 mb-2">
                    <label class="control-label" for="category_colSpan">
                        {{resource('DivisionMiscellaneousCategoryEditModel_ColSpan_DisplayName')}}
                    </label>
                </div>

                <div class="col-8 mb-2">
                    <dropdown
                        id="category_colSpan"
                        v-model.number="category.colSpan"
                        v-bind:options="colSpanOptions"
                    ></dropdown>
                    <required-error-message
                            v-if="colSpanIsMissing"
                            v-bind:field-name="resource('DivisionMiscellaneousCategoryEditModel_ColSpan_DisplayName')"
                    />
                </div>

            </div>
        </div>

        <div class="col-12 text-right" v-if="canAddFields">
            <button class="btn btn-primary" @click="addField">{{resource('SettingsDivisionMiscellaneous_Index_AddFieldButton_Text')}}</button>
        </div>
        
        <div class="col-12">
            <hr />
        </div>
        
        <div class="col-12">
            <div
                v-for="field in category.fields"
                :key="field.id"
            >
                <div class="row mb-2">
                    <div class="col-6">
                        <h4>{{resource('SettingsDivisionMiscellaneous_Index_FieldsHeader')}}</h4>
                    </div>
                    <div v-if="deletableFieldIds.indexOf(field.id) !== -1" class="col-6 text-right">
                        <button class="btn btn-danger" @click="deleteField(field)">
                            {{resource('SettingsDivisionMiscellaneous_Index_DeleteFieldButton_Text')}}
                        </button>
                    </div>
                </div>              

                <miscellaneous-field class="form-group" v-bind:field="field"></miscellaneous-field>
            </div>
        </div>
    </div>
</template>

<script>
    import settingsDivisionMiscellaneousApi from '../../../Api/settings-division-miscellaneous';
    import divisionApi from '../../../Api/divisions'
    import growler from "../../../Util/growler";
    import {guid} from "../../../Util/guid";
    
    import resourceMixin from '../../../Util/Mixins/resources';

    import MiscellaneousField from "./MiscellaneousField.vue";
    import DivisionDropdown from "../../../Components/Util/DivisionDropdown.vue";
    import Dropdown from "../../../Components/FormControls/Dropdown.vue";
    import RequiredErrorMessage from "../../../Components/Errors/RequiredErrorMessage.vue";
    
    export default {
        name: "miscellaneous-category",
        components: {Dropdown, DivisionDropdown, MiscellaneousField, RequiredErrorMessage},
        mixins: [resourceMixin],
        created(){
            const vm = this;
            
            divisionApi.getDivisionsList().then(
                divisions => vm.divisions = divisions,
                err => growler.bootstrapDanger(Array.isArray(err) ? err.join('\n') : err)
            );

            settingsDivisionMiscellaneousApi.validateWritePermission()
                .then(canWrite => vm.canWrite = canWrite);

            settingsDivisionMiscellaneousApi.validateDeletePermission()
                .then(canDelete => vm.canDelete = canDelete);
        },
        methods: {
            addField(){
                this.category.fields.push({
                    //Ensure at least an id is present as it serves as our object's key
                    id: guid(),
                    _isNew: true
                });
            },
            deleteField(field){
                const index = this.category.fields.findIndex(f => f.id === field.id);
                
                if(index !== -1){
                    this.category.fields.splice(index, 1);   
                }
            }
        },
        data: () => ({
            divisions: null,
            canDelete: false,
            canWrite: false
        }),
        computed: {
            colSpanOptions(){
                //4 - 12
                return [...Array(9).keys()].map(x => x + 4)
            },
            canAddFields(){
                return this.canWrite;
            },
            deletableFieldIds(){
                //Delete permission grants permission to delete all fields
                if(this.canDelete){
                    return this.category.fields.map(f => f.id);
                }
                
                //New fields have not yet been saved; and can thus be freely deleted
                return this.category.fields.reduce((fieldIds, field) => {
                    if(field._isNew){
                        fieldIds.push(field.id);
                    }
                    
                    return fieldIds;
                }, []);
            },
            nameIsMissing(){
                return !this.category.name;
            },
            sortOrderIsMissing(){
                return this.category.sortOrder === undefined;
            },
            colSpanIsMissing(){
                return this.category.colSpan === undefined;
            }
        },
        props: {
            category: {
                required: true,
                type: Object
            }
        }
    }
</script>